import React, { Dispatch, SetStateAction } from 'react';
import { CancelTokenSource } from 'axios';
import redux from 'redux';
import { getModalSwitch } from '@app/store/actions';
import { myProfileService } from '@app/services';
import { TypedTFunction, translations } from '@app/models';
import { ActiveMethodManageMfa, SetStepManageMfa, MfaDialogHandleClickPrimary } from '@app/models';
import { ManageMfaSteps, MethodsMfa } from '@app/enums';

export const handleCancelManageMfa = (dispatch: redux.Dispatch) => () => {
    dispatch(getModalSwitch(false));
};

export const getIsDisabledManageMfa = (
    stepManageMfa: ManageMfaSteps,
    activeMethod: ActiveMethodManageMfa,
    isDisableButtonNext: boolean,
    passCode: string
) => {
    const values = {
        [ManageMfaSteps.chooseMethod]: !activeMethod,
        [ManageMfaSteps.setUpDevice]: !passCode,
        [ManageMfaSteps.complete]: false
    };
    return values[stepManageMfa];
};

export const getPrimaryBtnNameManageMfa = (
    activeMethod: ActiveMethodManageMfa,
    stepManageMfa: ManageMfaSteps,
    translation: TypedTFunction<typeof translations>
) => {
    const names = {
        [ManageMfaSteps.chooseMethod]: translation('NEXT_BUTTON'),
        [ManageMfaSteps.setUpDevice]: translation('NEXT_BUTTON'),
        [ManageMfaSteps.complete]: translation('DONE')
    };
    return names[stepManageMfa];
};

export const getErrorMessageManageMfa = (
    translation: TypedTFunction<typeof translations>,
    errorCode: number
) => {
    switch (errorCode) {
        case 1:
            return translation('U2F_REGISTER_ERROR_TYPE_OTHER_ERROR');
        case 2:
            return translation('U2F_REGISTER_ERROR_TYPE_BAD_REQUEST');
        case 3:
            return translation('U2F_REGISTER_ERROR_TYPE_CONFIGURATION_UNSUPPORTED');
        case 4:
            return translation('U2F_REGISTER_ERROR_TYPE_DEVICE_INELIGIBLE');
        case 5:
            return translation('U2F_REGISTER_ERROR_TYPE_TIMEOUT');

        default:
            return '';
    }
};

export const getSecondaryBtnNameManageMfa = (
    stepManageMfa: ManageMfaSteps,
    translation: TypedTFunction<typeof translations>
) => {
    const names = {
        [ManageMfaSteps.chooseMethod]: translation('CANCEL'),
        [ManageMfaSteps.setUpDevice]: translation('CANCEL'),
        [ManageMfaSteps.complete]: ''
    };
    return names[stepManageMfa];
};

export const getDialogTitleManageMfa = (
    stepManageMfa: ManageMfaSteps,
    activeMethod: ActiveMethodManageMfa,
    translation: TypedTFunction<typeof translations>
) => {
    const titles = {
        [ManageMfaSteps.chooseMethod]: translation('SET_UP_MFA_HEADER'),
        [ManageMfaSteps.setUpDevice]:
            activeMethod !== MethodsMfa.u2f
                ? translation('SET_UP_AUTHENTICATOR')
                : translation('U2F_BEGIN_REGISTRATION_HEADER'),
        [ManageMfaSteps.complete]: ''
    };
    return titles[stepManageMfa];
};

const incrementStepValue = (setStepManageMfa: Dispatch<SetStateAction<ManageMfaSteps>>) => {
    setStepManageMfa((step) => step + 1);
};

export const decrementStepValue = (setStepManageMfa: Dispatch<SetStateAction<ManageMfaSteps>>) => {
    setStepManageMfa((step) => step - 1);
};

export const handleClickPrimaryManageMfa = ({
    setIsWaitResponse,
    setStepManageMfa,
    stepManageMfa,
    passCode,
    completeCb,
    verifyErrorCb
}: MfaDialogHandleClickPrimary) => (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    switch (stepManageMfa) {
        case ManageMfaSteps.chooseMethod:
            incrementStepValue(setStepManageMfa);
            break;
        case ManageMfaSteps.setUpDevice:
            setIsWaitResponse(true);
            myProfileService
              .mfaVerify(passCode)
              .then(() => {
                  completeCb();

                  setStepManageMfa(ManageMfaSteps.chooseMethod);
              })
              .catch(() => verifyErrorCb())
              .finally(() => {
                  setIsWaitResponse(false);
              });
            break;
        case ManageMfaSteps.complete: {
            completeCb();
            setStepManageMfa(ManageMfaSteps.chooseMethod);
            break;
        }
        default: {
            incrementStepValue(setStepManageMfa);
            break;
        }
    }
};

export const handleClickSecondaryManageMfa = (
    sourceCancel: CancelTokenSource,
    setStepManageMfa: SetStepManageMfa,
    dispatch: redux.Dispatch,
    setIsModalActive?: Function,
) => () => {
    sourceCancel.cancel();
    setStepManageMfa(ManageMfaSteps.chooseMethod);
    handleCancelManageMfa(dispatch)();
    setIsModalActive && setIsModalActive(false);
};

export const onClickTroubleManageMfa = (
    setIsDisableButtonNext: Dispatch<boolean>,
    setIsNeedExpandTokenBlock: Dispatch<SetStateAction<boolean>>
) => () => {
    setIsDisableButtonNext(false);
    setIsNeedExpandTokenBlock((isNeedExpandTokenBlock) => !isNeedExpandTokenBlock);
};

export const pushSetUpManageMfa = (
    registrationId: string,
    sourceCancel: CancelTokenSource,
    setIsUserPushActivated: Dispatch<boolean>,
    setStepManageMfa: SetStepManageMfa,
    errorCallback: Function,
    setCompleted?: () => void
) => {
    return myProfileService.mfaWaitForRegister(registrationId, sourceCancel.token).then(() => {
        setIsUserPushActivated(true);

        myProfileService.mfaWaitForPush(registrationId, errorCallback, () => {
            setCompleted && setCompleted();
            setStepManageMfa(ManageMfaSteps.complete);
        });
    });
};
